import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import {
  HtmlEditor,
  Image,
  Inject,
  QuickToolbar,
  RichTextEditorComponent,
  PasteCleanup,
  Table,
  Toolbar,
  Link,
} from "@syncfusion/ej2-react-richtexteditor";
import useStartupStore from "../stores/startupStore";
import { AssignMembers } from "../components/Startups/TextEditor";
import OutcomeValidation from "./OutcomeValidation";
import { Alert, Button, Tooltip, Checkbox } from "@mantine/core";
import RevisionHistoryViewer from "./RevisionHistoryViewer";
import { IconTools, IconToolsOff } from "@tabler/icons-react";
import useUserStore from "../stores/userStore";
import { IconInfoCircle } from "@tabler/icons-react";
import Mixpanel from "../components/Lib/mixpanel";
import { useTranslation } from "react-i18next";
import { useAutoSave, CompactSaveStatus } from "../components/AutoSave";
import SuggestedTasks from "./SuggestedTasks";

const TextCreationArea = ({ setMessage, loading, setShowAlert, showAlert, setShowTask }) => {
  const [showToolbar, setShowToolbar] = useState(true);
  const rteInstance = useRef(null);

  const blobUrlMap = useRef(new Map());
  const uploadedImageUrls = useRef([]);
  const {t}=useTranslation();

  const {
    content,
    setContent,
    setPrivacy,
    selectedProgram,
    isMember,
    isOwner,
    slug,
    isSubmitted
  } = useStartupStore((state) => ({
    content: state.content,
    setContent: state.setContent,
    setPrivacy: state.setPrivacy,
    selectedProgram: state.selectedProgram,
    isMember: state.isMember,
    isOwner: state.isOwner,
    slug: state.slug,
    isSubmitted: state.isSubmitted
  }));

  const {
    user: { enable_ai: ai },
  } = useUserStore((state) => state.user);

  const pageId = selectedProgram.cid;
  const index = selectedProgram.sid;

  // Get subcategory ID for auto-save
  const subcategoryId = content[pageId]?.[index]?.id;

  // Auto-save functionality
  const autoSave = useAutoSave(subcategoryId, {
    enabled: !!(isOwner || isMember) && !isSubmitted,
    debounceDelay: 1200,
    changeThreshold: 10,
    onSaveSuccess: () => {},
    onSaveError: () => {}
  });

  const [initialContent, setInitialContent] = useState("");
  // Removed in-editor AI toggle; assistant moved to right column

  useEffect(() => {
    // Initialize editor content only when switching page/subcategory
    // Prevents cursor jumping caused by re-binding value while typing
    if (content[pageId]?.[index]?.response) {
      setInitialContent(content[pageId][index].response);
    } else {
      setInitialContent("");
    }
  }, [pageId, index]);


  const saveEditorContent = useCallback(async () => {
    if (!rteInstance.current) return;
    try {
      let htmlContent = rteInstance.current.getHtml();

      if (htmlContent.includes("blob:") && blobUrlMap.current.size > 0) {
        blobUrlMap.current.forEach((permanentUrl, blobUrl) => {
          htmlContent = htmlContent.replace(blobUrl, permanentUrl);
        });
      }

      if (htmlContent.includes("blob:")) {
        // console.warn("Content still contains blob URLs which won't persist!");
      }

      // Save to local store
      setContent(htmlContent);
    } catch (error) {
      console.error("Failed to save editor content:", error);
    }
  }, [pageId, index, setContent]);

  useEffect(() => {
    if (!rteInstance.current || !rteInstance.current.element) return;

    const editorElement = rteInstance.current.element;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              let images = [];
              if (node.tagName === "IMG") {
                images.push(node);
              }
              if (node.querySelectorAll) {
                const containedImages = node.querySelectorAll("img");
                if (containedImages.length) {
                  images = [...images, ...containedImages];
                }
              }
              images.forEach((img) => {
                if (img.src && img.src.startsWith("blob:")) {
                  if (uploadedImageUrls.current.length > 0) {
                    const permanentUrl = uploadedImageUrls.current.shift();
                    blobUrlMap.current.set(img.src, permanentUrl);
                    img.src = permanentUrl;
                    saveEditorContent();
                  }
                }
              });
            }
          });
        }
      });
    });

    observer.observe(editorElement, {
      childList: true, subtree: true, attributes: false
    });

    return () => {
      observer.disconnect();
    };
  }, [rteInstance.current, saveEditorContent]);

  const insertImageSettings = useMemo(() => ({
    allowedTypes: [".jpeg", ".jpg", ".png", ".gif", ".svg"],
    display: "inline",
    saveUrl: "/rich_text_images/upload",
    removeUrl: "/rich_text_images/delete",
  }), []);

  // Debounce the handleChange to avoid excessive saving
  const debouncedSave = useCallback(debounce(saveEditorContent, 300), [saveEditorContent]);

  const handleChange = useCallback(() => {
    debouncedSave();
    if (rteInstance.current && subcategoryId && autoSave) {
      const htmlContent = rteInstance.current.getHtml();
      autoSave.saveContent(htmlContent);
    }
  }, [debouncedSave, subcategoryId, autoSave]);

  const readOnly = !(isOwner || isMember) || isSubmitted;

  return (
    <div>
      {(isOwner || isMember) && !isSubmitted && content[pageId]?.[index]?.outcome && showAlert && (
          <Tooltip position="bottom-start"
          withArrow transitionProps={{ transition: "pop", duration: 300 }} label="Expected Outcome" color="#6c45e7">
      <Alert 
        m={8} 
        variant="light" 
        color="blue" 
        withCloseButton 
        title="" 
        icon={<IconInfoCircle />}
        onClose={() => setShowAlert(false)}
      >      
        {content[pageId][index].outcome}  
      </Alert>
      </Tooltip>
    )}
      <div
        style={{
          justifyContent: "center",
          display: "flex",
          gap: "8px",
          marginBottom: "4px",
        }}
      >
        {!loading && (
          <div className="flex items-center justify-center gap-2">
            {(isOwner || isMember) && !showAlert && <Tooltip
              label="Show Outcome"
              position="bottom"
              withArrow
              color="#6c45e7"
              transitionProps={{ transition: "pop", duration: 300 }}
            >
              <Button
                size="xs"
                variant="light"
                onClick={() => setShowAlert(true)}
                color="#6c45e7"
                aria-label="Show Outcome"
              >
                <IconInfoCircle size={16} />
              </Button>
            </Tooltip>}
            {((isOwner || isMember) && !isSubmitted) && <Tooltip
              label={showToolbar ? t("startups_page.hide_toolbar") : t("startups_page.show_toolbar")}
              position="bottom"
              withArrow
              color="#6c45e7"
              transitionProps={{ transition: "pop", duration: 300 }}
            >
              <Button
                size="xs"
                variant="light"
                onClick={() => {
                  setShowToolbar((p) => {
                    const newState = !p;
                    Mixpanel.button(newState ? "Show Toolbar" : "Hide Toolbar", {
                      location: "Content Editor",
                      startup_slug: slug,
                    });
                    return newState;
                  });
                }}
                color="#6c45e7"
                aria-label={showToolbar ? "Hide toolbar" : "Show toolbar"}
              >
                {showToolbar ? (
                  <IconToolsOff size={16} />
                ) : (
                  <IconTools size={16} />
                )}
              </Button>
            </Tooltip>}
            {ai && !isSubmitted &&  <OutcomeValidation />}
            {!isSubmitted &&  <AssignMembers key={`${pageId}-${index}`} />}
            {(isOwner || isMember) && !isSubmitted && content?.[pageId]?.[index]?.id && (
               <RevisionHistoryViewer
                  startupEntitySlug={slug}
                  subCategoryId={content[pageId][index].id}
               />
            )}
            {subcategoryId && (isOwner || isMember) && !isSubmitted && autoSave.enabled && (
              <CompactSaveStatus subcategoryId={subcategoryId} />
            )}
          </div>
        )}
      </div>
      
      <div style={{ flex: 1, overflowY: "auto", minHeight: "500px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <RichTextEditorComponent
          ref={rteInstance}
          key={`${pageId}-${index}`}
          value={initialContent}
          placeholder={(isOwner || isMember) ? t("startups_page.details") : ""}
          enableResize={true}
          readonly={!(isOwner || isMember) || isSubmitted}
          toolbarSettings={useMemo(() => ({
            enable: showToolbar && (isOwner || isMember),
            type: "Scrollable",
            items: [
              "FullScreen","Bold", "Italic", "Underline", "StrikeThrough",
              "FontName", "FontSize", "FontColor", "BackgroundColor", "|",
              "Formats", "Alignments", "OrderedList", "UnorderedList",
              "Outdent", "Indent", "|",
              "CreateLink", "Image", "CreateTable", "|",
              "ClearFormat", "|",
              "Undo", "Redo",
            ],
          }), [showToolbar])}
          insertImageSettings={insertImageSettings}
          pasteCleanupSettings={useMemo(() => ({
            allowedStyleProps: ['color', 'margin', 'font-size'],
            deniedAttrs: ['class', 'title', 'id'],
            deniedTags: ['a'],
            keepFormat: false,
            plainText: false,
            prompt: true
          }), [])}
          beforeImageUpload={useCallback((args) => {
            if (args && args.currentRequest) {
              const metaTag = document.querySelector("meta[name='csrf-token']");
              const token = metaTag ? metaTag.content : "";
              args.currentRequest.setRequestHeader("X-CSRF-Token", token);
            }
            return true;
          }, [])}
          imageUploadSuccess={useCallback((args) => {
            try {
              let responseData;
              if (typeof args.e.target.response === "string") {
                responseData = JSON.parse(args.e.target.response);
              } else {
                responseData = args.e.target.response;
              }
              if (!responseData || !responseData.url) return;
              const permanentUrl = responseData.url;
              uploadedImageUrls.current.push(permanentUrl);
            } catch (error) {
              console.error("Error handling image upload success:", error);
            }
          }, [])}
          imageUploadFailed={useCallback((args) => {
            console.error("Image upload failed:", args);
          }, [])}
          change={handleChange}
          height="100%"
          saveInterval={200}
        >
          <Inject
            services={[Toolbar, HtmlEditor, Image, Link, Table, QuickToolbar, PasteCleanup]}
          />
        </RichTextEditorComponent>

        {(isOwner || isMember) && !isSubmitted && (
          <SuggestedTasks subcategoryId={subcategoryId} readOnly={readOnly} setShowTask={setShowTask} />
        )}
      </div>

    </div>
  );
};

export default React.memo(TextCreationArea);